import { useCallback, useEffect, useMemo, useState } from "react";

import { OBRAS_TABLE_COLUMNS_KEY, OBRAS_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { useMediaQuery } from "@/hooks/use-media-query";
import { createExportFeedback, describeReportFilters, useFileExport } from "@/modules/export";
import {
  DEFAULT_OBRAS_SORT,
  EMPTY_OBRAS_FILTERS,
  exportObras,
  useObraEmbarcacoes,
  useObraStatusTodos,
  useObraTiposObra,
} from "@/modules/obras";
import type { ObrasFilters, ObrasSort } from "@/modules/obras";
import { useSessionSavedFilters } from "@/shared/hooks";
import { TABLE_SETTINGS_APPLIED_MESSAGE, useTableSettings, useToast } from "@/ds";
import type {
  CalendarChangeEvent,
  FilterDefinition,
  FilterOption,
  FilterValue,
  SaveFilterResult,
} from "@/ds";

import {
  MOBILE_QUERY,
  OBRAS_ALL_COLUMNS,
  OBRAS_EXPORT_FILE_NAME,
  OBRAS_MAIN_COLUMNS,
  OBRAS_SEM_STATUS,
  OBRAS_TAB_DETALHAMENTO,
  OBRAS_TAB_INDICADORES,
  PERIOD_COLUMN,
  SAVED_FILTERS_SCREEN,
} from "./obras-lista.constants";
import type {
  DateRangeValue,
  ExportToastMessage,
  ObrasColumnKey,
  ObrasListResult,
  ObrasScreenViewModel,
  ObrasViewMode,
} from "./types-obras-lista";
import {
  formatObraPeriodo,
  fromSavedValue,
  migrateObrasTableSettings,
  rangeFromFilters,
  toIsoDate,
  toPanelValue,
  toSavedValue,
} from "./obras-lista.utils";

export function useObrasScreen(): ObrasScreenViewModel {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const toast = useToast();

  const [view, setView] = useState<ObrasViewMode>("lista");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<ObrasFilters>(EMPTY_OBRAS_FILTERS);
  const [sort, setSort] = useState<ObrasSort | null>(null);
  const [range, setRange] = useState<DateRangeValue>(null);
  // A migração é idempotente e roda antes do hook, no primeiro render.
  useState(migrateObrasTableSettings);
  const settings = useTableSettings<ObrasColumnKey>({
    densityKey: OBRAS_TABLE_DENSITY_KEY,
    columnsKey: OBRAS_TABLE_COLUMNS_KEY,
    allColumns: OBRAS_ALL_COLUMNS,
    mainColumns: OBRAS_MAIN_COLUMNS,
  });
  const [listResult, setListResult] = useState({ status: "loading", total: 0 });
  const [versaoDaLista, setVersaoDaLista] = useState(0);
  const contextoDaNovaObra = useMemo(
    () => ({
      recarregarLista: () => setVersaoDaLista((versao) => versao + 1),
      avisarSucesso: (mensagem: string, detalhe?: string) => toast.success(mensagem, detalhe),
    }),
    [toast],
  );
  const registrarResultadoDaLista = useCallback(
    (result: ObrasListResult) => setListResult(result),
    [],
  );

  const status = useObraStatusTodos();
  const embarcacoes = useObraEmbarcacoes();
  const tipos = useObraTiposObra();

  const {
    savedFilters,
    saveFilter,
    deleteFilter: deleteSavedFilter,
    applySavedFilter,
  } = useSessionSavedFilters(SAVED_FILTERS_SCREEN);

  const statusOptions = useMemo(() => {
    const options = status.data.map((option) => ({ code: option.codigo, label: option.descricao }));
    // RN02: "Sem status" fecha a lista, para Lista e Cards mostrarem o mesmo conjunto de obras.
    return status.status === "success" ? [...options, { ...OBRAS_SEM_STATUS }] : options;
  }, [status.data, status.status]);

  // O período do topo espelha o filtro aplicado quando ele muda por fora (filtro salvo, limpar).
  useEffect(() => {
    setRange((current) => {
      const next = rangeFromFilters(filters);
      const complete = current?.[0] && current[1];
      if (!next && !complete && current) return current; // intervalo parcial em construção
      return next;
    });
  }, [filters]);

  /** Todas as colunas de filtro; também dão os rótulos do resumo de filtros da exportação. */
  const allFilterColumns = useMemo<(FilterDefinition & { options: FilterOption[] })[]>(
    () => [
      {
        key: "statusObra",
        label: "Status da Obra",
        options: statusOptions.map((option) => ({ value: option.code, label: option.label })),
        isLoading: status.status === "loading",
        hasError: status.status === "error" || status.status === "forbidden",
        type: "multiselect",
        searchPlaceholder: "Buscar status",
      },
      {
        key: "embarcacaoId",
        label: "Embarcação",
        options: embarcacoes.data.map((option) => ({ value: option.id, label: option.nome })),
        isLoading: embarcacoes.status === "loading",
        hasError: embarcacoes.status === "error" || embarcacoes.status === "forbidden",
        type: "multiselect",
        searchPlaceholder: "Buscar embarcação",
      },
      {
        key: "tipoObra",
        label: "Tipos de Obra",
        options: tipos.data.map((option) => ({ value: option.codigo, label: option.codigo })),
        isLoading: tipos.status === "loading",
        hasError: tipos.status === "error" || tipos.status === "forbidden",
        type: "multiselect",
        searchPlaceholder: "Buscar tipo de obra",
      },
      // O filtro Condição (Seco/Molhado) fica fora até haver contrato (Impasse 4 da HU).
    ],
    [statusOptions, status.status, embarcacoes, tipos],
  );

  /**
   * Colunas do painel de filtros: no mobile Status da Obra e Data saem do topo e entram no card
   * (Figma 11468:82509, casos 8 e 9).
   */
  const filterColumns = useMemo<FilterDefinition[]>(() => {
    const byKey = (key: string) => allFilterColumns.filter((column) => column.key === key);

    return isMobile
      ? [
          ...byKey("embarcacaoId"),
          ...byKey("tipoObra"),
          ...byKey("statusObra"),
          { key: PERIOD_COLUMN, label: "Data", type: "date-range" as const },
        ]
      : allFilterColumns.filter((column) => column.key !== "statusObra");
  }, [allFilterColumns, isMobile]);

  const panelKeys = useMemo(() => filterColumns.map((column) => column.key), [filterColumns]);
  const panelValue = useMemo<FilterValue>(
    () => Object.fromEntries(panelKeys.map((key) => [key, toPanelValue(filters)[key] ?? []])),
    [panelKeys, filters],
  );

  /** O painel conhece só as próprias colunas; os demais critérios do conjunto ficam como estão. */
  const panelSavedFilters = useMemo(
    () =>
      savedFilters.map((saved) => ({
        ...saved,
        value: Object.fromEntries(
          panelKeys.map((key) => [key, toPanelValue(fromSavedValue(saved.value))[key] ?? []]),
        ),
      })),
    [savedFilters, panelKeys],
  );

  function handlePanelChange(value: FilterValue) {
    // O período só vale completo (início e fim), como no campo do topo.
    const periodo = value[PERIOD_COLUMN] ?? [];
    const complete = periodo.length === 2;

    setFilters((current) => ({
      ...current,
      ...(isMobile
        ? {
            statusObra: value.statusObra ?? [],
            dataInicio: complete ? (periodo[0] ?? "") : "",
            dataFim: complete ? (periodo[1] ?? "") : "",
          }
        : {}),
      embarcacaoId: value.embarcacaoId ?? [],
      tipoObra: value.tipoObra ?? [],
    }));
  }

  /** RN06: o filtro salvo guarda o conjunto completo (status, período, embarcação e tipo). */
  function handleSaveFilter(name: string, value: FilterValue): SaveFilterResult {
    const { [PERIOD_COLUMN]: periodo, ...rest } = value;
    const complete = periodo?.length === 2;

    return saveFilter(name, {
      ...toSavedValue(filters),
      ...rest,
      ...(periodo
        ? {
            dataInicio: complete ? [periodo[0] ?? ""] : [],
            dataFim: complete ? [periodo[1] ?? ""] : [],
          }
        : {}),
    });
  }

  /** Recupera o conjunto salvo inteiro, inclusive o que fica fora do painel. */
  function handleApplySavedFilter(id: string) {
    applySavedFilter(id, (value) => setFilters(fromSavedValue(value)));
  }

  function handleRange(value: CalendarChangeEvent<"range">["value"]) {
    const next: DateRangeValue = value ? [value[0] ?? null, value[1] ?? null] : null;
    setRange(next);

    if (!next || (!next[0] && !next[1])) {
      setFilters((current) => ({ ...current, dataInicio: "", dataFim: "" }));
    } else if (next[0] && next[1]) {
      setFilters((current) => ({
        ...current,
        dataInicio: toIsoDate(next[0] as Date),
        dataFim: toIsoDate(next[1] as Date),
      }));
    }
  }

  // O feedback de exportação ainda fala com o `ref` do Prime; este adaptador o liga ao `useToast` do DS.
  const toastRef = useMemo(
    () => ({
      current: {
        show: ({ severity, summary, detail, life }: ExportToastMessage) =>
          toast.show({ severity, summary, detail, life }),
      },
    }),
    [toast],
  );

  /**
   * Exportar Visualização (HU 2531): o servidor gera o arquivo com busca, filtros e ordenação em
   * vigor, sem paginação, e só as colunas visíveis da Lista na ordem da tela (a lupa fica fora).
   * Na visão Cards o arquivo sai na visão de tabela, com as colunas visíveis da Lista.
   */
  const exportView = useFileExport({
    fileName: OBRAS_EXPORT_FILE_NAME,
    loadFile: (format) => {
      return exportObras({
        busca: search,
        filters,
        filtros: describeReportFilters(search, allFilterColumns, toSavedValue(filters), {
          periodo: formatObraPeriodo(filters),
        }),
        sort: view === "cards" ? DEFAULT_OBRAS_SORT : (sort ?? DEFAULT_OBRAS_SORT),
        colunas: settings.visibleColumns,
        format,
      });
    },
    isEmpty: () => view === "lista" && listResult.status === "success" && listResult.total === 0,
    ...createExportFeedback(toastRef),
  });

  function handleApplySettings() {
    toast.success("Sucesso", TABLE_SETTINGS_APPLIED_MESSAGE);
  }

  function buscar(texto: string) {
    setSearch(texto);
  }

  function clearSearch() {
    setSearch("");
  }

  function escolherVisao(visao: ObrasViewMode) {
    setView(visao);
  }

  function ordenarPor(ordem: ObrasSort | null) {
    setSort(ordem);
  }

  function clearFilters() {
    setFilters(EMPTY_OBRAS_FILTERS);
    setRange(null);
  }

  function clearStatus() {
    setFilters((current) => ({ ...current, statusObra: [] }));
  }

  function changeStatus(statusObra: string[]) {
    setFilters((current) => ({ ...current, statusObra }));
  }

  const tabOptions = [
    { label: OBRAS_TAB_INDICADORES, value: "indicadores", disabled: true },
    { label: OBRAS_TAB_DETALHAMENTO, value: "detalhamento" },
  ];

  return {
    isMobile,
    view,
    escolherVisao,
    search,
    buscar,
    tabOptions,
    filters,
    sort,
    ordenarPor,
    range,
    handleRange,
    settings,
    registrarResultadoDaLista,
    isStatusDisabled: status.status !== "success",
    statusState: status.status,
    statusError: status.errorMessage,
    statusOptions,
    filterColumns,
    panelValue,
    panelSavedFilters,
    handlePanelChange,
    handleSaveFilter,
    handleApplySavedFilter,
    deleteSavedFilter,
    exportView,
    handleApplySettings,
    clearSearch,
    clearFilters,
    clearStatus,
    changeStatus,
    versaoDaLista,
    contextoDaNovaObra,
  };
}
